import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, type Role } from '../../auth/auth.context'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { LanguageSwitcher } from '../../components/language-switcher'
import {
  clearPendingPlan,
  destinationAfterAuth,
  marketingPlanPriceLabel,
  parseMarketingPlan,
  postAuthPath,
  readPendingPlan,
  withPlanQuery,
} from '../billing/marketing-plan'
import { useMarketingPlan } from '../billing/use-marketing-plan'
import { ErrorMessage } from './components/error-message'

const inputClass =
  'w-full rounded-2xl border border-sky-100 bg-white/90 px-4 py-3 text-[#12304a] shadow-[0_4px_16px_rgba(56,189,248,0.08)] outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-sky-300 focus:border-sky-300 focus:ring-4 focus:ring-sky-200/60 disabled:cursor-not-allowed disabled:opacity-50'

const labelClass = 'block text-sm font-semibold text-[#12304a]'

const primaryButtonClass =
  'w-full rounded-2xl bg-gradient-to-r from-sky-400 to-blue-400 px-6 py-3.5 font-semibold text-white shadow-[0_14px_32px_rgba(56,189,248,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:brightness-[1.02] hover:shadow-[0_20px_42px_rgba(56,189,248,0.28)] focus:outline-none focus:ring-4 focus:ring-sky-200/70 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'

const pageBackgroundStyle = {
  background:
    'radial-gradient(circle at top left, rgba(125, 211, 252, 0.22), transparent 30%), radial-gradient(circle at 85% 12%, rgba(96, 165, 250, 0.16), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f4faff 38%, #eaf6ff 100%)',
}

const gridBackgroundStyle = {
  backgroundImage:
    'linear-gradient(rgba(186, 230, 253, 0.32) 1px, transparent 1px), linear-gradient(90deg, rgba(186, 230, 253, 0.32) 1px, transparent 1px)',
  backgroundSize: '44px 44px',
  maskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
  WebkitMaskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent 88%)',
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { login, mfaVerify, user, isLoading: authLoading } = useAuth()
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const plan = useMarketingPlan()

  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [passwordSetSuccess, setPasswordSetSuccess] = useState(false)

  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')

  useEffect(() => {
    if (searchParams.get('passwordSet') === 'true') {
      setPasswordSetSuccess(true)
      const pending = parseMarketingPlan(searchParams.get('plan')) ?? readPendingPlan()
      nav(pending ? `/login?plan=${pending}` : '/login', { replace: true })
    }
  }, [searchParams, nav])

  useEffect(() => {
    const queryPlan = parseMarketingPlan(searchParams.get('plan'))
    if (authLoading || !user || !queryPlan) return
    if (user.role === 'EMPLOYEE') return
    nav(postAuthPath(queryPlan), { replace: true })
  }, [authLoading, user, searchParams, nav])

  const continueAfterAuth = (role?: Role) => {
    const pending = readPendingPlan()
    if (role === 'EMPLOYEE') clearPendingPlan()
    nav(destinationAfterAuth(role, role === 'EMPLOYEE' ? null : pending))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const email = emailRef.current?.value ?? ''
    const password = passwordRef.current?.value ?? ''

    setError(undefined)
    setLoading(true)

    try {
      const result = await login(email, password)

      if (result?.mfaRequired) {
        setMfaChallengeId('pending')
      } else {
        continueAfterAuth(result?.role)
      }
    } catch (e: any) {
      setError(e?.userMessage || e?.response?.data?.message || t('auth.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!mfaChallengeId) return

    setError(undefined)
    setLoading(true)

    try {
      const role = await mfaVerify(mfaChallengeId, mfaCode)
      continueAfterAuth(role)
    } catch (e: any) {
      setError(e?.userMessage || e?.response?.data?.message || t('auth.mfaFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className='relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10 text-[#12304a]'
      style={pageBackgroundStyle}
    >
      <LanguageSwitcher variant='auth' className='fixed absolute top-4 right-4 z-20' />

      <div className='pointer-events-none fixed inset-0' style={gridBackgroundStyle} />

      <div className='pointer-events-none absolute -right-24 top-20 h-[460px] w-[min(48vw,580px)] rounded-full bg-gradient-to-br from-sky-300/25 to-blue-300/20 blur-3xl' />
      <div className='pointer-events-none absolute -bottom-28 -left-20 h-80 w-80 rounded-full bg-sky-200/30 blur-3xl' />

      <div className='relative z-10 w-full max-w-md animate-slide-up'>
        <div className='rounded-[2rem] border border-sky-200/80 bg-white/90 p-8 shadow-[0_16px_50px_rgba(56,189,248,0.10)] backdrop-blur-2xl transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_20px_56px_rgba(14,165,233,0.14)]'>
          <div className='mb-8 text-center'>
            <div className='mb-5 inline-flex h-30 w-30 items-center justify-center rounded-2xl bg-gray-50 shadow-[0_14px_32px_rgba(56,189,248,0.18)]'>
              <img
                src='/images/icons/logo.png'
                alt={t('auth.startSmartLogo')}
                className='h-28 w-auto'
              />
            </div>

            <h1 className='mb-2 text-3xl font-bold tracking-tight text-slate-950'>
              {t('auth.welcomeBack')}
            </h1>

            <p className='text-sm text-slate-600'>{t('auth.loginSubtitle')}</p>
          </div>

          {plan && (
            <div
              data-testid='marketing-plan-banner'
              className='mb-6 rounded-2xl border border-sky-200 bg-sky-50/90 p-4 text-sm leading-6 text-sky-900'
            >
              {t('auth.selectedPlanBanner', {
                licenses: plan,
                price: marketingPlanPriceLabel(plan),
              })}
            </div>
          )}

          {passwordSetSuccess && (
            <div className='mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 animate-slide-up'>
              <div className='flex items-center'>
                <svg
                  className='mr-2 h-5 w-5 text-emerald-500'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>

                <span className='text-sm text-emerald-700'>{t('auth.passwordSetSuccess')}</span>
              </div>
            </div>
          )}

          {mfaChallengeId ? (
            <form onSubmit={handleMfaSubmit} className='space-y-6'>
              <div className='mb-2 rounded-2xl border border-sky-200 bg-sky-50/90 p-4'>
                <p className='text-sm text-sky-800'>{t('auth.mfaHint')}</p>
              </div>

              <div className='space-y-2'>
                <label htmlFor='mfaCode' className={labelClass}>
                  {t('auth.mfaCode')}
                </label>

                <input
                  type='text'
                  id='mfaCode'
                  inputMode='numeric'
                  pattern='[0-9]{6}'
                  maxLength={6}
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder='123456'
                  required
                  disabled={loading}
                  autoFocus
                  className={`${inputClass} text-center text-2xl tracking-widest`}
                />
              </div>

              {error && <ErrorMessage message={error} />}

              <button
                type='submit'
                disabled={loading || mfaCode.length !== 6}
                className={primaryButtonClass}
              >
                {loading ? t('common.verifying') : t('common.verify')}
              </button>

              <button
                type='button'
                onClick={() => {
                  setMfaChallengeId(null)
                  setMfaCode('')
                  setError(undefined)
                }}
                className='w-full rounded-xl py-2 text-sm font-medium text-slate-600 transition-colors hover:text-sky-700'
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <label htmlFor='email' className={labelClass}>
                    {t('auth.emailAddress')}
                  </label>

                  <input
                    type='email'
                    id='email'
                    ref={emailRef}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    disabled={loading}
                    className={inputClass}
                  />
                </div>

                <div className='space-y-2'>
                  <label htmlFor='password' className={labelClass}>
                    {t('auth.password')}
                  </label>

                  <input
                    type='password'
                    id='password'
                    ref={passwordRef}
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    disabled={loading}
                    className={inputClass}
                  />
                </div>
              </div>

              {error && <ErrorMessage message={error} />}

              <button type='submit' disabled={loading} className={primaryButtonClass}>
                {loading ? (
                  <div className='flex items-center justify-center'>
                    <svg
                      className='-ml-1 mr-3 h-5 w-5 animate-spin text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      />

                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      />
                    </svg>
                    {t('common.processing')}
                  </div>
                ) : (
                  t('auth.login')
                )}
              </button>

              <div className='text-center'>
                <Link
                  to='/auth/forgot-password'
                  className='text-sm font-medium text-slate-600 transition-colors hover:text-sky-700'
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </form>
          )}

          <div className='mt-6 text-center'>
            <p className='text-sm text-slate-600'>
              {t('auth.noAccount')}{' '}
              <Link
                to={withPlanQuery('/signup', plan)}
                className='font-semibold text-sky-700 transition-colors duration-200 hover:text-sky-600'
              >
                {t('auth.registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes slide-up {
              from {
                opacity: 0;
                transform: translateY(24px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .animate-slide-up {
              animation: slide-up 0.55s ease-out;
            }
          `,
        }}
      />
    </div>
  )
}
